import { Box } from '@mui/material';
import { KitButton, SecH } from '../common/MobileKit';
import { protoIcon } from '../common/ProtoIcon';
import {
    KitAccordion,
    KitBanner,
    KitField,
    KitFormBar,
    KitInput,
    KitInputButton,
    KitInputIco,
    KitSearch,
    KitSwitch,
} from '../common/MobileKitExtras';
import FkField from '../common/MobileFormFields';

/**
 * Formulários do grupo Sistema no shell mobile — `prototipos-mobile/sistema/usuario-form.html`
 * e `perfil-form.html`. Só apresentação: estado, validação, permissões e envio ficam em
 * `pages/users/Form.js` e `pages/profiles/Form.js`.
 */

const EyeIcon = protoIcon('eye');
const EyeOffIcon = protoIcon('eyeoff');
const ShieldIcon = protoIcon('shield');
const LockIcon = protoIcon('lock');

function PasswordField({ formik, name, label, required, visible, onToggle, ...rest }) {
    return (
        <FkField
            formik={formik}
            name={name}
            label={label}
            required={required}
            type={visible ? 'text' : 'password'}
            autoComplete="new-password"
            sx={{ pr: '48px' }}
            wrap={(body) => (
                <KitInputIco
                    rightButtons={(
                        <KitInputButton onClick={onToggle} aria-label={visible ? `Ocultar ${label.toLowerCase()}` : `Mostrar ${label.toLowerCase()}`}>
                            {visible ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
                        </KitInputButton>
                    )}
                >
                    {body}
                </KitInputIco>
            )}
            {...rest}
        />
    );
}

export function MobileUserForm({
    formik,
    profiles,
    isEdit,
    showPassword,
    onTogglePassword,
    showMasterKey,
    onToggleMasterKey,
    onCancel,
    onSave,
    loading,
}) {
    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }} component="form" onSubmit={formik.handleSubmit} noValidate>
            <FkField formik={formik} name="name" label="Nome" required type="text" />
            <FkField formik={formik} name="last_name" label="Sobrenome" required type="text" />
            <FkField formik={formik} name="username" label="Nome de usuário" required type="text" autoComplete="off" autoCapitalize="none" />
            <FkField formik={formik} name="profile" label="Perfil" required as="select">
                <option value="">Selecione</option>
                {profiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>{profile.name}</option>
                ))}
            </FkField>
            {!isEdit ? (
                <PasswordField
                    formik={formik}
                    name="password"
                    label="Senha"
                    required
                    visible={showPassword}
                    onToggle={onTogglePassword}
                />
            ) : (
                <Box sx={{ fontSize: 12, color: 'text.disabled', m: '-4px 2px 14px', lineHeight: 1.4 }}>
                    A senha não é alterada por aqui — para redefini-la, use uma ação administrativa separada.
                </Box>
            )}
            <PasswordField
                formik={formik}
                name="master_key"
                label="Senha mestre"
                visible={showMasterKey}
                onToggle={onToggleMasterKey}
                help="Usada para autorizar operações sensíveis, como cancelar itens, alterar quantidade de produtos e cancelar vendas."
            />
            <KitSwitch
                title="É um Mecânico"
                description="Disponibiliza este usuário para ser atribuído como mecânico responsável em ordens de serviço"
                checked={Boolean(formik.values.is_mechanic)}
                onChange={(e) => formik.setFieldValue('is_mechanic', e.target.checked)}
            />

            <KitFormBar>
                <KitButton onClick={onCancel}>Cancelar</KitButton>
                <KitButton variant="primary" onClick={onSave} disabled={loading}>
                    {loading ? 'Salvando...' : 'Salvar'}
                </KitButton>
            </KitFormBar>
        </Box>
    );
}

export function MobileProfileForm({
    name,
    onNameChange,
    locked,
    isEdit,
    adminFlag,
    adminDisabled,
    onToggleAdmin,
    searchTerm,
    onSearchChange,
    modules,
    onToggleModule,
    isSearching,
    matrixLocked,
    checkedPerms,
    onToggleAll,
    onTogglePerm,
    emptyText,
    onCancel,
    onSave,
    saving,
}) {
    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            {locked && (
                <Box sx={{ mb: '14px' }}>
                    <KitBanner title="Perfil de sistema — somente leitura" icon={LockIcon}>
                        Perfis padrão do sistema (como Administrador) já vêm com todas as permissões concedidas e não podem ser renomeados nem ter suas permissões alteradas por aqui.
                    </KitBanner>
                </Box>
            )}

            <KitField label="Nome do perfil" required>
                <KitInput value={name} onChange={(e) => onNameChange(e.target.value)} disabled={locked} type="text" />
            </KitField>
            {!isEdit && (
                <KitSwitch
                    title="Perfil administrador"
                    description="Marca o perfil como administrador e concede todas as permissões."
                    checked={adminFlag}
                    disabled={adminDisabled}
                    onChange={(e) => onToggleAdmin(e.target.checked)}
                />
            )}

            <SecH title="Permissões" />
            <Box sx={{ mb: '12px' }}>
                <KitSearch
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Buscar módulo ou permissão…"
                    inputProps={{ 'aria-label': 'Buscar módulo ou permissão', inputMode: 'search' }}
                />
            </Box>

            {modules.map(({ mod, permsToShow, isOpen }) => {
                const checkedCount = mod.perms.filter((p) => checkedPerms.has(p.id)).length;
                const allChecked = checkedCount === mod.perms.length;
                return (
                    <KitAccordion
                        key={mod.key}
                        open={isOpen}
                        icon={ShieldIcon}
                        title={mod.label}
                        subtitle={`${checkedCount} de ${mod.perms.length} permissões`}
                        onToggle={() => onToggleModule(mod.key)}
                    >
                        <Box sx={{ mt: '-10px' }}>
                            {!isSearching && (
                                <KitSwitch
                                    title="Selecionar tudo"
                                    checked={allChecked}
                                    disabled={matrixLocked}
                                    onChange={(e) => onToggleAll(mod, e.target.checked)}
                                />
                            )}
                            {permsToShow.map((perm) => (
                                <KitSwitch
                                    key={perm.id}
                                    title={perm.label}
                                    checked={checkedPerms.has(perm.id)}
                                    disabled={matrixLocked}
                                    onChange={() => onTogglePerm(perm.id)}
                                />
                            ))}
                        </Box>
                    </KitAccordion>
                );
            })}

            {modules.length === 0 && (
                <Box sx={{ p: '28px 16px', textAlign: 'center', color: 'text.disabled', fontSize: 13, border: '1px dashed', borderColor: 'divider', borderRadius: '16px' }}>
                    {emptyText}
                </Box>
            )}

            <KitFormBar>
                <KitButton onClick={onCancel}>{locked ? 'Fechar' : 'Cancelar'}</KitButton>
                {!locked && (
                    <KitButton variant="primary" onClick={onSave} disabled={saving}>
                        {saving ? 'Salvando...' : 'Salvar'}
                    </KitButton>
                )}
            </KitFormBar>
        </Box>
    );
}
